import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VerifiedBadge from "../components/VerifiedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArtistsDirectoryPage() {
  const [artists, setArtists] = useState([]);
  const [search, setSearch] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "100" });
    if (search) params.set("search", search);
    if (verifiedOnly) params.set("verified_only", "true");
    fetch(`${API}/artists?${params}`)
      .then((r) => r.json())
      .then((d) => setArtists(Array.isArray(d) ? d.filter(a => a.slug) : []))
      .catch(() => setArtists([]))
      .finally(() => setLoading(false));
  }, [search, verifiedOnly]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-16 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <h1 className="font-heading text-4xl sm:text-5xl font-black mb-2 flex items-center gap-3">
          <Users size={32} className="text-[#FF007F]" />
          Artistas
        </h1>
        <p className="text-zinc-400 mb-8">
          {loading ? "Carregando artistas..." : `Os ${artists.length} artistas oficiais do Arrocha Play`}
        </p>

        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <div className="relative flex-1 min-w-[280px] max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="search"
              placeholder="Buscar artista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="artists-search"
              className="w-full bg-zinc-900 border border-white/10 rounded-full pl-12 pr-4 py-3 text-sm focus:border-[#FF007F] focus:outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={verifiedOnly}
              onChange={(e) => setVerifiedOnly(e.target.checked)}
              className="w-4 h-4 accent-[#FF007F]"
            />
            Apenas verificados
          </label>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="aspect-square rounded-full bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : artists.length === 0 ? (
          <p className="text-center text-zinc-500 py-20">Nenhum artista encontrado.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {artists.map((a) => (
              <Link key={a.id} to={`/artista/${a.slug}`} className="group text-center" data-testid={`artist-card-${a.slug}`}>
                <div className="aspect-square rounded-full overflow-hidden bg-zinc-900 ring-2 ring-transparent group-hover:ring-[#FF007F]/50 transition mb-3">
                  {a.photo_url ? (
                    <img
                      src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`}
                      alt={a.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-black text-zinc-600">
                      {a.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <p className="font-bold text-white text-sm flex items-center justify-center gap-1.5 truncate">
                  <span className="truncate">{a.name}</span>
                  {a.verified && <VerifiedBadge size={14} />}
                </p>
                <p className="text-xs text-zinc-500">{a.followers_count?.toLocaleString("pt-BR") || 0} seguidores</p>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
